'use client';

import { useState, useEffect, Fragment, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isSafeExternalUrl } from '@/components/icons/social-icons';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import { cn, truncateAddress } from '@/lib/utils';
import {
  getCachedMentionDisplayName,
  resolveMentionDisplayName,
} from '@/lib/mentions/resolve';

const MENTION_REGEX = /@\[(0x[a-fA-F0-9]{40})\]/g;

function renderWithNewlines(text: string): ReactNode[] {
  const lines = text.split('\n');
  if (lines.length === 1) return [text];
  const result: ReactNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) result.push(<br key={`br-${i}`} />);
    if (line) result.push(line);
  });
  return result;
}

const TRUSTED_DOMAINS = new Set([
  'x.com',
  'twitter.com',
  'takeover.fun',
  'flaunch.gg',
  'basescan.org',
  'base.org',
]);

const URL_REGEX = /https?:\/\/[^\s<>)"']+/gi;

function getDomain(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function isTrustedUrl(url: string): boolean {
  const domain = getDomain(url);
  if (!domain) return false;
  return TRUSTED_DOMAINS.has(domain) || Array.from(TRUSTED_DOMAINS).some(d => domain.endsWith(`.${d}`));
}

type TextPart =
  | { type: 'text'; value: string }
  | { type: 'link'; url: string }
  | { type: 'mention'; address: string };

function MentionSpan({ address, isOwn }: { address: string; isOwn?: boolean }) {
  // Seed from the shared cache so a remount of the chat panel (or a
  // repeat mention of the same address in another message) renders the
  // resolved name on the first paint without firing another request.
  const [displayName, setDisplayName] = useState<string | null>(
    () => getCachedMentionDisplayName(address) ?? null,
  );
  const profileModal = useProfileModalOptional();

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedMentionDisplayName(address);
    if (cached !== undefined) {
      setDisplayName(cached);
      return () => { cancelled = true; };
    }
    resolveMentionDisplayName(address)
      .then((name) => { if (!cancelled) setDisplayName(name); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [address]);

  const className = cn(
    "font-medium hover:underline",
    isOwn
      ? "text-amber-100/95 underline decoration-amber-400/45"
      : "text-amber-200/90 hover:text-amber-50 underline decoration-amber-500/30"
  );
  const label = `@${displayName || truncateAddress(address)}`;

  if (!profileModal) {
    return <span className={className}>{label}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => profileModal.openProfile(address)}
      className={className}
    >
      {label}
    </button>
  );
}

export function ExternalLinkText({ text, isOwn }: { text: string; isOwn?: boolean }) {
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const parts: TextPart[] = [];
  let lastIndex = 0;

  const combined = new RegExp(
    `${MENTION_REGEX.source}|${URL_REGEX.source}`,
    'gi',
  );

  for (const match of text.matchAll(combined)) {
    const start = match.index!;
    if (start > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, start) });
    }

    if (match[1]) {
      parts.push({ type: 'mention', address: match[1] });
    } else {
      parts.push({ type: 'link', url: match[0] });
    }

    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  if (parts.length === 0) {
    return <span>{renderWithNewlines(text)}</span>;
  }

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'text') {
          return <Fragment key={i}>{renderWithNewlines(part.value)}</Fragment>;
        }
        if (part.type === 'mention') {
          return <MentionSpan key={i} address={part.address} isOwn={isOwn} />;
        }
        const trusted = isTrustedUrl(part.url);
        return (
          <a
            key={i}
            href={trusted ? part.url : undefined}
            target={trusted ? '_blank' : undefined}
            rel={trusted ? 'noopener noreferrer' : undefined}
            onClick={trusted ? undefined : (e) => { e.preventDefault(); setPendingUrl(part.url); }}
            className={cn(
              'hover:underline cursor-pointer break-all',
              isOwn
                ? 'text-amber-100/95 underline decoration-amber-400/45'
                : 'text-amber-200/90 hover:text-amber-50 underline decoration-amber-500/30',
            )}
          >
            {part.url}
          </a>
        );
      })}

      <Dialog open={!!pendingUrl} onOpenChange={(open) => !open && setPendingUrl(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Leaving Takeover
            </DialogTitle>
            <DialogDescription className="text-left pt-2 space-y-2">
              <p>You are about to visit an external link:</p>
              <p className="font-mono text-xs bg-muted/50 rounded-md px-3 py-2 break-all text-foreground">
                {pendingUrl}
              </p>
              <p>
                This link is <span className="font-semibold text-foreground">not verified</span> and could be misleading.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setPendingUrl(null)}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                if (pendingUrl && isSafeExternalUrl(pendingUrl)) {
                  window.open(pendingUrl, '_blank', 'noopener,noreferrer');
                }
                setPendingUrl(null);
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
